/**
 * Custom hook for Solar Design location search
 * Uses a backend geocode proxy (direct browser calls to Nominatim are CORS-blocked)
 */
import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";

const GEOCODE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/solar-designs/geocode`;

export function useSolarSearch(mapRef) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);
  const resultsRef = useRef([]);

  /**
   * Accept coordinates directly, in any of the common forms people paste:
   *   28.4595, 77.0266   |   28.4595 77.0266   |   28°27'34"N 77°01'36"E
   *   https://maps.google.com/...@28.4595,77.0266,18z   |   ...?q=28.4595,77.0266
   *   https://earth.google.com/web/@28.4595,77.0266,...
   */
  const parseCoordinates = (raw) => {
    if (!raw) return null;
    let text = raw.trim();
    const url = text.match(/@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/) || text.match(/[?&](?:q|ll|query)=(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/);
    if (url) return { lat: parseFloat(url[1]), lng: parseFloat(url[2]) };
    const dms = text.match(/(\d{1,2})[°\s](\d{1,2})['′\s](\d{1,2}(?:\.\d+)?)["″\s]*([NS])[,\s]+(\d{1,3})[°\s](\d{1,2})['′\s](\d{1,2}(?:\.\d+)?)["″\s]*([EW])/i);
    if (dms) {
      const lat = (+dms[1] + dms[2] / 60 + dms[3] / 3600) * (dms[4].toUpperCase() === "S" ? -1 : 1);
      const lng = (+dms[5] + dms[6] / 60 + dms[7] / 3600) * (dms[8].toUpperCase() === "W" ? -1 : 1);
      return { lat, lng };
    }
    const dec = text.match(/^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/);
    if (dec) {
      const lat = parseFloat(dec[1]), lng = parseFloat(dec[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }
    return null;
  };

  const runSearch = useCallback(async (val) => {
    const coords = parseCoordinates(val);
    if (coords) {
      const r = [{ display_name: `Coordinates ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`, lat: String(coords.lat), lon: String(coords.lng), source: "coords" }];
      resultsRef.current = r;
      setSearchResults(r);
      return r;
    }
    setSearchLoading(true);
    try {
      const resp = await axios.get(GEOCODE_URL, { params: { q: val } });
      const data = resp.data || [];
      resultsRef.current = data;
      setSearchResults(data);
      if (!data.length) toast.info("No match — try adding the city/state, or paste coordinates (e.g. 28.4595, 77.0266) or a Google Maps link");
      return data;
    } catch (err) {
      console.error(err);
      toast.error("Location search unavailable right now");
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchInput = useCallback((val) => {
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 3) { setSearchResults([]); resultsRef.current = []; return; }
    if (parseCoordinates(val)) { runSearch(val); return; } // instant, no network
    debounceRef.current = setTimeout(() => runSearch(val), 400);
  }, [runSearch]);

  const goToLocation = useCallback((result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lng], 19, { duration: 1.5 });
    }
    setSearchQuery(result.display_name);
    setSearchResults([]);
    resultsRef.current = [];
    toast.success("Location found");
  }, [mapRef]);

  // Enter key: use the first current result, or fetch immediately if none loaded yet
  const submitSearch = useCallback(async () => {
    clearTimeout(debounceRef.current);
    let list = resultsRef.current;
    if (!list.length && searchQuery.trim().length >= 3) list = await runSearch(searchQuery);
    if (list.length) goToLocation(list[0]);
  }, [searchQuery, runSearch, goToLocation]);

  return {
    searchQuery, setSearchQuery,
    searchResults, setSearchResults,
    searchLoading,
    handleSearchInput,
    goToLocation,
    submitSearch,
  };
}
